import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';

interface ZoomContextType {
  zoomLevel: number;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  setZoom: (level: number) => void;
}

const ZoomContext = createContext<ZoomContextType | undefined>(undefined);

export const useZoomContext = () => {
  const context = useContext(ZoomContext);
  if (!context) {
    throw new Error('useZoomContext must be used within a ZoomProvider');
  }
  return context;
};

interface ZoomProviderProps {
  children: ReactNode;
}

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3.0;
const ZOOM_STEP = 0.1;
const DEFAULT_ZOOM = 1.0;

export const ZoomProvider: React.FC<ZoomProviderProps> = ({ children }) => {
  const [zoomLevel, setZoomLevel] = useState(() => {
    const savedZoom = localStorage.getItem('app-zoom-level');
    return savedZoom ? parseFloat(savedZoom) : DEFAULT_ZOOM;
  });

  useEffect(() => {
    localStorage.setItem('app-zoom-level', zoomLevel.toString());
  }, [zoomLevel]);

  const zoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  }, []);

  const resetZoom = useCallback(() => {
    setZoomLevel(DEFAULT_ZOOM);
  }, []);

  const setZoom = useCallback((level: number) => {
    const clampedLevel = Math.min(Math.max(level, MIN_ZOOM), MAX_ZOOM);
    setZoomLevel(clampedLevel);
  }, []);

  const value = {
    zoomLevel,
    zoomIn,
    zoomOut,
    resetZoom,
    setZoom,
  };

  return (
    <ZoomContext.Provider value={value}>
      {children}
    </ZoomContext.Provider>
  );
};
